import React, { useCallback } from 'react';
import { SCORE_OPTIONS, SCORE_COLORS } from '../types';
import { ScorecardLayoutProps } from './types';

/**
 * Carousel layout — compact stepper showing prev / current / next with
 * arrow buttons on either side. The original scorecard presentation.
 */
const ScorecardCarouselLayout: React.FC<ScorecardLayoutProps> = ({
  currentScore,
  onSelect,
  readOnly,
}) => {
  const lastIndex = SCORE_OPTIONS.length - 1;
  const currentIndex = currentScore
    ? SCORE_OPTIONS.findIndex(o => o.value === currentScore)
    : -1;
  const isEmpty = currentIndex === -1;

  const currentOption = currentIndex >= 0 ? SCORE_OPTIONS[currentIndex] : null;
  const selectedColors = currentOption ? SCORE_COLORS[currentOption.value] : null;

  // The target each control would select. From the empty state both
  // directions are valid entry points (stepping down lands on '--', stepping
  // up lands on '++'); without this the carousel had no enabled control and
  // could never be filled in.
  const prevOption =
    !isEmpty && currentIndex > 0 ? SCORE_OPTIONS[currentIndex - 1]
    : isEmpty ? SCORE_OPTIONS[0]
    : null;
  const nextOption =
    !isEmpty && currentIndex < lastIndex ? SCORE_OPTIONS[currentIndex + 1]
    : isEmpty ? SCORE_OPTIONS[lastIndex]
    : null;

  const go = useCallback(
    (direction: -1 | 1) => {
      if (readOnly) return;
      // From empty, a down-step (−1) enters at '--' and an up-step (+1) at '++'.
      const nextIndex =
        currentIndex === -1
          ? (direction === -1 ? 0 : SCORE_OPTIONS.length - 1)
          : currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= SCORE_OPTIONS.length) return;
      onSelect(SCORE_OPTIONS[nextIndex].value);
    },
    [readOnly, currentIndex, onSelect],
  );

  return (
    <div className="h-full flex items-center justify-center bg-surface">
      <div className="flex items-center gap-3">
        {/* Left arrow */}
        <div className="w-4">
          {prevOption && (
            <button
              type="button"
              onClick={() => go(-1)}
              disabled={readOnly}
              aria-label="Previous"
              className="p-0.5 text-neutral-500 hover:text-neutral-300 transition-colors disabled:cursor-default"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Carousel */}
        <div className="flex items-center gap-2">
          {/* Previous */}
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={readOnly || !prevOption}
            className={`w-7 h-7 rounded-md border border-neutral-600 flex items-center justify-center text-[10px] font-semibold text-neutral-500 select-none ${prevOption ? 'opacity-40 hover:opacity-70 cursor-pointer' : 'opacity-0 cursor-default'} transition-opacity disabled:cursor-default`}
          >
            {prevOption?.label ?? ''}
          </button>

          {/* Selected */}
          <div
            className={[
              'w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold select-none',
              currentOption && selectedColors
                ? `${selectedColors.bg} ${selectedColors.text}`
                : 'border border-neutral-600 text-neutral-500',
            ].join(' ')}
          >
            {currentOption?.label ?? '—'}
          </div>

          {/* Next */}
          <button
            type="button"
            onClick={() => go(1)}
            disabled={readOnly || !nextOption}
            className={`w-7 h-7 rounded-md border border-neutral-600 flex items-center justify-center text-[10px] font-semibold text-neutral-500 select-none ${nextOption ? 'opacity-40 hover:opacity-70 cursor-pointer' : 'opacity-0 cursor-default'} transition-opacity disabled:cursor-default`}
          >
            {nextOption?.label ?? ''}
          </button>
        </div>

        {/* Right arrow */}
        <div className="w-4">
          {nextOption && (
            <button
              type="button"
              onClick={() => go(1)}
              disabled={readOnly}
              aria-label="Next"
              className="p-0.5 text-neutral-500 hover:text-neutral-300 transition-colors disabled:cursor-default"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ScorecardCarouselLayout);
